import { createSlice } from "@reduxjs/toolkit";
import { checkSession, onLogin, updateWidgetSettingsThunk } from "./thunks";
import * as userSelectors from "./selectors";
import { extraReducers } from "./extraReducers";

const initialState = {
  checkSession: {
    status: "idle",
    sessionValid: true,
  },
  token: localStorage.getItem("token"),
  appUserId: "",
  organizationId: "",
  firstName: "",
  lastName: "",
  email: "",

  // ** Start of Top nav Bar Search Items ** - This is used by the top nav search bar to communicate with the splash page.
  // We need to break this up into its own slice. But for now it lives here.

  debouncedValue: "",

  // We need something to store the initial results of whatever is searched. That way we can resotre defaults when someone clears a selection.
  initialResults: [],

  // Results is what gets appended to the table when a user searches for something.
  results: [],

  // This is what the user has selected in the top nav bar search.
  searchedValue: {},

  // These are the results that are shown in the top nav bar search dropdown.
  searchResults: [],

  // This is the text that the user has typed into the top nav bar search.
  searchText: "",

  // When viewing assetSnapshot/:assetId or inventorySnapshot/:assetId, the loaded asset for the top bar.
  currentSnapshotAsset: null,

  // ** End of Top Nav Bar Search Items **

  // Whether or not a user wants to use the browsers timezone as their default
  userTimeZoneUseDefault: true,

  // e.g., admin, editor, etc.
  usersConsoleRole: "",

  // User actions, e.g., available actions, edit asset, delete batch, edit facility, etc.
  userActions: [],

  // Users chosen timezone
  userTimeZone: "",

  // e.g., [mobile, console, emailOptOut]
  userPermissions: [],

  // e.g., what pages the user can see
  viewPermissions: {
    showAssetHistory: true,
    showBatches: true,
    showDevices: true,
    showFacilities: true,
    showInventory: true,
    showOrganizations: true,
    showProducts: true,
    showSettings: true,
    showUsers: true,
  },

  // this will be where all the user's preferences, e.g., table and filter settings, will be kept
  userPropertiesMap: {
    propertiesMap: {
      assetHistoryTableSettings: {},
      assetSnapshotSettings: {},
      assetStatusTableSettings: {},
      inventoryStatusTableSettings: {},
      inventoryHistoryTableSettings: {},
      batchHistoryTableSettings: {},
      consoleSettings: {},
      deviceTableSettings: {},
      timeZone: {},
      widgetSettings: {},
    },
  },
};

export const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {

    // This is used by the top nav bar, this houses the debounced value for what the user has typed in.
    setDebouncedValue: (state, action) => {
      state.debouncedValue = action.payload;
    },

    // This is used by the top nav bar, this houses the rows that are appened on the mvpAsset page.
    setResults(state, action) {
      state.results = action.payload;
    },

    // This is used by the top nav bar, this houses the default rows that are appened on the mvpAsset page.
    setInitialResults(state, action) {
      state.initialResults = action.payload;
    },

    // This is used by the top nav search bar, this houses the results for the category selector.
    setSearchResults: (state, action) => {
      state.searchResults = action.payload;
    },

    // This is used by the top nav bar, this houses which option the user has clicked on from the category search
    setSearchedValue: (state, action) => {
      state.searchedValue = action.payload;
    },

    // This is used by the top nav bar, this houses what searched text
    setSearchText: (state, action) => {
      state.searchText = action.payload;
    },

    // Set/clear the asset shown in the top bar when on assetSnapshot or inventorySnapshot page
    setCurrentSnapshotAsset: (state, action) => {
      state.currentSnapshotAsset = action.payload;
    },

    updateUserPermissions: (state, action) => {
      state.userPermissions = action.payload;
    },
    // update from settings/user page
    updateUserSettings: (state, action) => {
      state.userPropertiesMap.propertiesMap = action.payload.propertiesMap;
      state.email = action.payload.email;
    },
    // update widgets from dashboard
    updateWidgetSettings: (state, action) => {
      // object path may be undefined
      state.userPropertiesMap.propertiesMap.widgetSettings =
        state.userPropertiesMap.propertiesMap.widgetSettings || {};
      // spreading settings so we can e.g. update one property at a time without overwriting all properties
      const widgetSettingsFromStore =
        state.userPropertiesMap.propertiesMap.widgetSettings[
        action.payload.widget
        ] || {};

      const spreadSettings = {
        ...widgetSettingsFromStore,
        ...action.payload.settings,
      };

      state.userPropertiesMap.propertiesMap.widgetSettings[
        action.payload.widget
      ] = spreadSettings;
    },
    updateUserPropertiesMap: (state, action) => {
      state.userPropertiesMap.propertiesMap = {
        ...state.userPropertiesMap.propertiesMap,
        ...action.payload,
      };
    },
    updateUserTimezone: (state, action) => {
      state.userTimeZone = action.payload.timeZone;
      state.userTimeZoneUseDefault = action.payload.userTimeZoneUseDefault;
    },

    onLogout: () => {
      localStorage.clear();
      return {
        ...initialState,
        checkSession: {
          status: "idle",
          sessionValid: false,
        },
      };
    },
  },
  extraReducers: {
    // look for async thunk states, conditionally update state based on thunk state
    ...extraReducers,
  },
});

// Action creators are generated for each case reducer function
const {
  onLogout,
  setCurrentSnapshotAsset,
  setDebouncedValue,
  setInitialResults,
  setResults,
  setSearchedValue,
  setSearchResults,
  setSearchText,
  updateUserPermissions,
  updateUserPreferences,
  updateUserPropertiesMap,
  updateUserSettings,
  updateUserTimezone,
  updateWidgetSettings,
} = userSlice.actions;

export {
  checkSession,
  onLogin,
  onLogout,
  setCurrentSnapshotAsset,
  setDebouncedValue,
  setInitialResults,
  setResults,
  setSearchedValue,
  setSearchResults,
  setSearchText,
  updateUserPermissions,
  updateUserPreferences,
  updateUserPropertiesMap,
  updateUserSettings,
  updateUserTimezone,
  updateWidgetSettings,
  updateWidgetSettingsThunk,
  userSelectors,
};

// export the default reducer to be used in the store configuration
export default userSlice.reducer;
